class SorellaWeather extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    try { this.products = JSON.parse(this.querySelector('[data-weather-products]')?.textContent || '[]'); }
    catch (_) { this.products = []; }
    this.trigger = this.querySelector('[data-weather-open]');
    this.triggerLabel = this.querySelector('[data-weather-trigger-label]');
    this.callout = this.querySelector('[data-weather-callout]');
    this.calloutMeta = this.querySelector('[data-weather-callout-meta]');
    this.calloutCopy = this.querySelector('[data-weather-callout-copy]');
    this.panel = this.querySelector('[data-weather-panel]');
    this.backdrop = this.querySelector('.sweather__backdrop');
    this.statusText = this.querySelector('[data-weather-status-text]');
    this.locationText = this.querySelector('[data-weather-location]');
    this.temperatureText = this.querySelector('[data-weather-temperature]');
    this.summary = this.querySelector('[data-weather-summary]');
    this.fallback = this.querySelector('[data-weather-fallback]');
    this.results = this.querySelector('[data-weather-results]');
    this.grid = this.querySelector('[data-weather-result-grid]');
    this.disclosure = this.querySelector('[data-weather-disclosure]');
    this.weatherIcons = {
      cold: this.dataset.weatherIconCold,
      cool: this.dataset.weatherIconCool,
      pleasant: this.dataset.weatherIconPleasant,
      hot: this.dataset.weatherIconHot,
      extremeHeat: this.dataset.weatherIconExtremeHeat,
      rain: this.dataset.weatherIconRain,
    };
    this.dynamicWeatherIcons = [...this.querySelectorAll('[data-weather-dynamic-icon]')];
    this.productStickyAtc = document.querySelector('.dpdp-sticky-atc');
    this.detected = false;
    this.abortController = null;
    this.lastFocus = null;
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundStickySync = this.syncStickyOffset.bind(this);
    try {
      if (sessionStorage.getItem('sorella-weather-trigger-collapsed-v1')) this.classList.add('is-collapsed');
    } catch (_) {}
    if (this.productStickyAtc) {
      this.stickyObserver = new MutationObserver(this.boundStickySync);
      this.stickyObserver.observe(this.productStickyAtc, { attributes: true, attributeFilter: ['hidden', 'style', 'class'] });
      this.stickyResizeObserver = new ResizeObserver(this.boundStickySync);
      this.stickyResizeObserver.observe(this.productStickyAtc);
      window.addEventListener('resize', this.boundStickySync, { passive: true });
      this.syncStickyOffset();
    }
    this.trigger?.addEventListener('click', () => this.open());
    this.querySelector('[data-weather-callout-open]')?.addEventListener('click', () => {
      this.dismissCallout();
      this.open();
    });
    this.querySelector('[data-weather-callout-close]')?.addEventListener('click', () => this.dismissCallout());
    this.querySelectorAll('[data-weather-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    this.querySelectorAll('[data-weather-manual]').forEach((button) => {
      button.addEventListener('click', () => this.showManualSeason(button.dataset.weatherManual));
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-add-to-cart]')) setTimeout(() => this.close(false), 0);
    });
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.revealTimer = window.setTimeout(() => {
      this.classList.add('is-revealed');
      if (!this.detected) {
        this.detected = true;
        this.detect({ announce: true });
      }
    }, reducedMotion ? 0 : Number(this.dataset.revealDelay || 2000));
  }

  disconnectedCallback() {
    this.abortController?.abort();
    clearTimeout(this.revealTimer);
    clearTimeout(this.calloutTimer);
    this.stickyObserver?.disconnect();
    this.stickyResizeObserver?.disconnect();
    window.removeEventListener('resize', this.boundStickySync);
    document.removeEventListener('keydown', this.boundKeydown);
  }

  syncStickyOffset() {
    const sticky = this.productStickyAtc;
    const visible = Boolean(sticky && !sticky.hidden && getComputedStyle(sticky).display !== 'none');
    this.classList.toggle('is-above-sticky-atc', visible);
    if (!visible) {
      this.style.removeProperty('--weather-sticky-offset');
      return;
    }
    const height = Math.ceil(sticky.getBoundingClientRect().height);
    this.style.setProperty('--weather-sticky-offset', `${Math.max(78, height + 12)}px`);
  }

  open() {
    if (!this.panel || this.classList.contains('is-open')) return;
    this.dismissCallout();
    this.classList.remove('is-collapsed');
    this.lastFocus = document.activeElement;
    this.classList.add('is-open');
    this.panel.setAttribute('aria-hidden', 'false');
    this.trigger?.setAttribute('aria-expanded', 'true');
    if (this.backdrop) this.backdrop.hidden = false;
    document.documentElement.classList.add('sweather-open');
    document.addEventListener('keydown', this.boundKeydown);
    requestAnimationFrame(() => this.panel.querySelector('[data-weather-close]')?.focus({ preventScroll: true }));
    if (!this.detected) {
      this.detected = true;
      this.detect({ announce: false });
    }
  }

  close(restoreFocus = true) {
    if (!this.panel || !this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (this.backdrop) this.backdrop.hidden = true;
    document.documentElement.classList.remove('sweather-open');
    document.removeEventListener('keydown', this.boundKeydown);
    this.classList.add('is-collapsed');
    try { sessionStorage.setItem('sorella-weather-trigger-collapsed-v1', '1'); } catch (_) {}
    if (restoreFocus && this.lastFocus instanceof HTMLElement) this.lastFocus.focus({ preventScroll: true });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('a,button,[tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.disabled && !element.hidden && element.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  async fetchWithTimeout(url, ms) {
    const controller = new AbortController();
    this.abortController = controller;
    const timer = setTimeout(() => controller.abort(), ms);
    try {
      const response = await fetch(url, { signal: controller.signal, referrerPolicy: 'no-referrer' });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return await response.json();
    } finally {
      clearTimeout(timer);
      if (this.abortController === controller) this.abortController = null;
    }
  }

  async detect({ announce = false } = {}) {
    try {
      const cached = this.readCachedWeather();
      if (cached) {
        this.showDetectedWeather(cached, announce);
        return;
      }
      const location = await this.findApproximateLocation();
      const { latitude, longitude } = location;
      const weather = await this.fetchWithTimeout(`https://api.open-meteo.com/v1/forecast?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}&current=temperature_2m,weather_code,is_day`, 6500);
      const temperature = Number(weather?.current?.temperature_2m);
      if (!Number.isFinite(temperature)) throw new Error('Missing temperature');
      const detected = { city: location.city, temperature, weatherCode: Number(weather?.current?.weather_code), isDay: Number(weather?.current?.is_day), savedAt: Date.now() };
      try { sessionStorage.setItem('sorella-weather-v2', JSON.stringify(detected)); } catch (_) {}
      this.showDetectedWeather(detected, announce);
    } catch (_) {
      this.showFallback(announce);
    }
  }

  async findApproximateLocation() {
    const providers = [
      { url: 'https://ipwho.is/', city: (data) => data.city || data.region, valid: (data) => data.success !== false },
      { url: 'https://get.geojs.io/v1/ip/geo.json', city: (data) => data.city || data.region },
      { url: 'https://ipapi.co/json/', city: (data) => data.city || data.region },
    ];
    for (const provider of providers) {
      try {
        const data = await this.fetchWithTimeout(provider.url, 5000);
        if (provider.valid && !provider.valid(data)) continue;
        const latitude = Number(data?.latitude);
        const longitude = Number(data?.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
        return { latitude, longitude, city: provider.city(data) || 'Your area' };
      } catch (_) {}
    }
    const timezoneFallbacks = {
      'Africa/Cairo': { latitude: 30.0444, longitude: 31.2357, city: 'Cairo' },
      'Africa/Alexandria': { latitude: 31.2001, longitude: 29.9187, city: 'Alexandria' },
    };
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezoneFallbacks[timezone]) return timezoneFallbacks[timezone];
    throw new Error('Location providers unavailable');
  }

  readCachedWeather() {
    try {
      const cached = JSON.parse(sessionStorage.getItem('sorella-weather-v2') || 'null');
      if (!cached || Date.now() - Number(cached.savedAt) > 30 * 60 * 1000) return null;
      if (!Number.isFinite(Number(cached.temperature))) return null;
      return cached;
    } catch (_) { return null; }
  }

  showDetectedWeather({ city = 'Your area', temperature, weatherCode = 2, isDay = null }, announce = false) {
    const season = window.SorellaWeatherLogic?.mapTemperatureToSeason(temperature);
    if (!season) throw new Error('Missing temperature');
    const hour = new Date().getHours();
    const daypart = window.SorellaWeatherLogic?.daypartFrom(isDay, hour) || 'day';
    this.dataset.weatherDaypart = daypart;
    this.dataset.weatherKind = this.weatherKindFor(weatherCode);
    this.applyWeatherIcon(temperature, weatherCode);
    this.locationText.textContent = city;
    this.statusText.textContent = 'Right now';
    this.temperatureText.textContent = `${Math.round(temperature)}°`;
    this.summary.textContent = this.summaryFor(season, daypart);
    this.triggerLabel.textContent = `${Math.round(temperature)}° in ${city}`;
    this.disclosure.hidden = false;
    this.showForSeason(season, temperature, city, { isDay, hour });
    if (announce) this.showCallout(city, temperature, weatherCode, isDay);
  }

  weatherKindFor(code) {
    const value = Number(code);
    if ((value >= 51 && value <= 82) || value >= 95) return 'rain';
    if (value === 0) return 'sunny';
    if (value === 1 || value === 2) return 'partly';
    return 'cloudy';
  }

  weatherIconKeyFor(temperature, weatherCode) {
    if (this.weatherKindFor(weatherCode) === 'rain') return 'rain';
    const degrees = Number(temperature);
    if (!Number.isFinite(degrees)) return 'pleasant';
    if (degrees < 10) return 'cold';
    if (degrees < 20) return 'cool';
    if (degrees < 30) return 'pleasant';
    if (degrees < 40) return 'hot';
    return 'extremeHeat';
  }

  applyWeatherIcon(temperature, weatherCode) {
    const key = this.weatherIconKeyFor(temperature, weatherCode);
    const source = this.weatherIcons[key] || this.weatherIcons.pleasant;
    this.dataset.weatherIconState = key;
    if (!source) return;
    this.dynamicWeatherIcons.forEach((image) => {
      if (image.getAttribute('src') !== source) image.setAttribute('src', source);
    });
  }

  weatherCalloutFor(city, temperature, weatherCode, isDay = null) {
    const place = city || 'Your city';
    const degrees = Math.round(temperature);
    const daypart = window.SorellaWeatherLogic?.daypartFrom(isDay, new Date().getHours()) || 'day';
    if (this.weatherKindFor(weatherCode) === 'rain') return `${place} brought rain? Plot twist. We picked three scents for it.`;
    if (daypart === 'night' && degrees >= 24) return `${place} is still at ${degrees}° after dark. Tropy Kiss and Tan Line understood the assignment.`;
    if (degrees >= 35) return `${place} is serving ${degrees}°. Pick something fresh before the sun steals the spotlight.`;
    if (degrees >= 29) return `${place} is ${degrees}° and feeling extra. Your scent edit is ready.`;
    if (degrees >= 21) return `${place} is behaving at ${degrees}°. Suspicious—but perfect for perfume.`;
    if (degrees >= 14) return `${place} found a breeze at ${degrees}°. Time for something with depth.`;
    return `${place} is ${degrees}°. Yes, really. Bring the warm scents.`;
  }

  showCallout(city, temperature, weatherCode, isDay = null) {
    if (!this.callout || this.classList.contains('is-open')) return;
    try { if (sessionStorage.getItem('sorella-weather-callout-v1')) return; } catch (_) {}
    this.calloutMeta.textContent = `WEATHER IN ${String(city || 'YOUR CITY').toUpperCase()} · ${Math.round(temperature)}°`;
    this.calloutCopy.textContent = this.weatherCalloutFor(city, temperature, weatherCode, isDay);
    this.callout.hidden = false;
    requestAnimationFrame(() => this.callout.classList.add('is-visible'));
    try { sessionStorage.setItem('sorella-weather-callout-v1', String(Date.now())); } catch (_) {}
    clearTimeout(this.calloutTimer);
    this.calloutTimer = window.setTimeout(() => this.dismissCallout(), 12000);
  }

  dismissCallout() {
    if (!this.callout || this.callout.hidden) return;
    clearTimeout(this.calloutTimer);
    this.callout.classList.remove('is-visible');
    window.setTimeout(() => { if (!this.callout.classList.contains('is-visible')) this.callout.hidden = true; }, 260);
  }

  summaryFor(season, daypart = 'day') {
    if (season === 'Summer' && daypart === 'night') return 'Still warm after dark. Go bright, juicy and made for plans.';
    return ({ Winter: 'Cold air. Let the scent bring the warmth.', Fall: 'A cooler day made for something with depth.', Spring: 'Easy weather, bright notes, no overthinking.', Summer: 'It’s warm out. Keep the scent light, bright or delicious.' })[season] || 'A small scent edit for right now.';
  }

  showFallback(announce = false) {
    this.detected = false;
    this.dataset.weatherKind = 'partly';
    this.applyWeatherIcon(null, null);
    this.locationText.textContent = 'CHOOSE YOUR WEATHER';
    this.statusText.textContent = 'How does today feel?';
    this.temperatureText.textContent = '—°';
    this.summary.textContent = 'Pick the closest match and we’ll make your scent edit.';
    this.fallback.hidden = false;
    if (announce && this.callout) {
      try {
        if (!sessionStorage.getItem('sorella-weather-callout-v1')) {
          this.calloutMeta.textContent = 'TODAY HAS A MOOD';
          this.calloutCopy.textContent = 'Weather is playing hard to get. Your nose still has options.';
          this.callout.hidden = false;
          requestAnimationFrame(() => this.callout.classList.add('is-visible'));
          sessionStorage.setItem('sorella-weather-callout-v1', String(Date.now()));
          this.calloutTimer = window.setTimeout(() => this.dismissCallout(), 12000);
        }
      } catch (_) {}
    }
  }

  showManualSeason(season) {
    const label = ({ Winter: 'Cold', Fall: 'Cool', Spring: 'Warm', Summer: 'Hot' })[season] || 'Today';
    this.locationText.textContent = 'YOUR PICK';
    this.statusText.textContent = `${label} outside`;
    this.temperatureText.textContent = '—';
    const hour = new Date().getHours();
    const daypart = window.SorellaWeatherLogic?.daypartFrom(null, hour) || 'day';
    this.dataset.weatherDaypart = daypart;
    this.summary.textContent = this.summaryFor(season, daypart);
    this.triggerLabel.textContent = `${label}-day edit`;
    const manualTemperature = ({ Winter: 6, Fall: 15, Spring: 24, Summer: 34 })[season];
    this.applyWeatherIcon(manualTemperature, 0);
    this.showForSeason(season, null, null, { hour });
  }

  showForSeason(season, temperature, city, context = {}) {
    const logic = window.SorellaWeatherLogic;
    if (!logic) return this.showFallback();
    const matches = logic.rankProducts(this.products, season, 3, context);
    this.fallback.hidden = true;
    this.results.hidden = false;
    const reason = logic.reasonForWeather(season, temperature, city, context);
    this.grid.innerHTML = matches.length
      ? matches.map((product, index) => this.card(product, index, reason)).join('')
      : '<p class="sweather__empty">No in-stock scent matches today yet. Try the full collection.</p>';
  }

  card(product, index, reason) {
    const logic = window.SorellaWeatherLogic;
    const image = product.variant.image || product.image || '';
    const separator = String(product.url || '').includes('?') ? '&' : '?';
    const url = `${product.url}${separator}variant=${encodeURIComponent(product.variant.id)}`;
    return `<article class="sweather-card"><a class="sweather-card__media" href="${logic.escapeHtml(url)}"><span>0${index + 1}</span><img src="${logic.escapeHtml(image)}" alt="${logic.escapeHtml(product.title)}" loading="lazy"></a><div class="sweather-card__copy"><p>${logic.escapeHtml(reason)}</p><h4><a href="${logic.escapeHtml(url)}">${logic.escapeHtml(product.title)}</a></h4>${product.character ? `<small>${logic.escapeHtml(product.character)}</small>` : ''}<div><span>${logic.escapeHtml(product.variant.title)} · ${logic.escapeHtml(product.variant.price)}</span><button type="button" data-add-to-cart data-variant-id="${logic.escapeHtml(product.variant.id)}" aria-label="Add ${logic.escapeHtml(product.title)} to cart">Add</button></div><p data-add-error role="status"></p></div></article>`;
  }
}
if (!customElements.get('sorella-weather')) customElements.define('sorella-weather', SorellaWeather);
